import { useState, useEffect, useCallback } from 'react';
import {
  getDemoDatabase,
  seedDemoProducts,
  DEMO_PRODUCTS,
  type DemoRow,
} from '../../models/demo';
import {
  getDemoRow,
  parseJsonArray,
  readBool,
  readNumber,
  readText,
  updateDemoRow,
} from '../../models/demo/sql';
import type { ProductVariant } from '../../redux/productSlice';
import { getProductKind, type ProductKind } from '../../utils/productKind';

export interface DemoProductItem {
  _id: string;
  bp_owner_id: string;
  bp_product_location_id: string;
  bp_product_barcode: string;
  bp_product_name: string;
  bp_product_category: string;
  bp_product_sub_category: string;
  bp_product_price_type: 'item' | 'unit';
  bp_product_kind: ProductKind;
  bp_product_unit_of_mass: string;
  bp_product_mass: string;
  bp_product_buying_price: number | null;
  bp_product_selling_price: number | null;
  bp_product_quantity: number | null;
  bp_product_size: string;
  bp_minimum_stock_level: number;
  bp_product_model: string;
  bp_product_image: string;
  bp_product_deleted: boolean;
  bp_product_is_favourite: boolean;
  bp_product_created_at: number;
  bp_product_updated_at: number;
  bp_product_has_variants: boolean;
  bp_product_variant_attributes: string[];
  bp_product_variants: ProductVariant[];
}

interface UseDemoProductsResult {
  products: DemoProductItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  searchProducts: (query: string) => Promise<void>;
  filterByCategory: (category: string | null) => Promise<void>;
  getCategories: () => string[];
}

const normalizePriceType = (value: string): 'item' | 'unit' =>
  value === 'unit' ? 'unit' : 'item';

function toDemoProductItem(row: DemoRow<'demo_products'>): DemoProductItem {
  const hasVariants = readBool(row.bp_product_has_variants);
  return {
    _id: row.id,
    bp_owner_id: readText(row.bp_owner_id),
    bp_product_location_id: readText(row.bp_product_location_id),
    bp_product_barcode: readText(row.bp_product_barcode),
    bp_product_name: readText(row.bp_product_name),
    bp_product_category: readText(row.bp_product_category),
    bp_product_sub_category: readText(row.bp_product_sub_category),
    bp_product_price_type: normalizePriceType(
      readText(row.bp_product_price_type),
    ),
    bp_product_kind: getProductKind(row),
    bp_product_unit_of_mass: readText(row.bp_product_unit_of_mass),
    bp_product_mass: readText(row.bp_product_mass),
    // Match real ProductObject: variant parents expose null at root price/qty
    bp_product_buying_price: hasVariants
      ? null
      : readNumber(row.bp_product_buying_price),
    bp_product_selling_price: hasVariants
      ? null
      : readNumber(row.bp_product_selling_price),
    bp_product_quantity: hasVariants
      ? null
      : readNumber(row.bp_product_quantity),
    bp_product_size: readText(row.bp_product_size),
    bp_minimum_stock_level: readNumber(row.bp_minimum_stock_level),
    bp_product_model: readText(row.bp_product_model),
    bp_product_image: readText(row.bp_product_image),
    bp_product_deleted: readBool(row.bp_product_deleted),
    bp_product_is_favourite: readBool(row.bp_product_is_favourite),
    bp_product_created_at: readNumber(row.bp_product_created_at),
    bp_product_updated_at: readNumber(row.bp_product_updated_at),
    bp_product_has_variants: hasVariants,
    bp_product_variant_attributes: parseJsonArray<string>(
      row.bp_product_variant_attributes,
    ),
    bp_product_variants: parseJsonArray<ProductVariant>(
      row.bp_product_variants,
    ),
  };
}

export function useDemoProducts(): UseDemoProductsResult {
  const [products, setProducts] = useState<DemoProductItem[]>([]);
  const [allProducts, setAllProducts] = useState<DemoProductItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Ensure demo products are seeded first
      await seedDemoProducts();

      const db = await getDemoDatabase();
      const fetchedProducts = await db.getAll<DemoRow<'demo_products'>>(
        'SELECT * FROM demo_products WHERE bp_product_deleted = 0',
      );

      const catalogOrder = new Map(
        DEMO_PRODUCTS.map((p, index) => [p.bp_product_name, index]),
      );
      const productItems = fetchedProducts
        .map(toDemoProductItem)
        .sort((a, b) => {
          const ai =
            catalogOrder.get(a.bp_product_name) ?? Number.MAX_SAFE_INTEGER;
          const bi =
            catalogOrder.get(b.bp_product_name) ?? Number.MAX_SAFE_INTEGER;
          return ai - bi;
        });
      setAllProducts(productItems);
      setProducts(productItems);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load demo products';
      setError(errorMessage);
      console.error('[useDemoProducts] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const searchProducts = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setProducts(allProducts);
        return;
      }

      const lowerQuery = query.toLowerCase();
      const filtered = allProducts.filter(
        product =>
          product.bp_product_name.toLowerCase().includes(lowerQuery) ||
          product.bp_product_barcode.includes(query) ||
          product.bp_product_category.toLowerCase().includes(lowerQuery),
      );
      setProducts(filtered);
    },
    [allProducts],
  );

  const filterByCategory = useCallback(
    async (category: string | null) => {
      if (!category) {
        setProducts(allProducts);
        return;
      }

      const filtered = allProducts.filter(
        product => product.bp_product_category === category,
      );
      setProducts(filtered);
    },
    [allProducts],
  );

  const getCategories = useCallback((): string[] => {
    const categorySet = new Set(allProducts.map(p => p.bp_product_category));
    return Array.from(categorySet);
  }, [allProducts]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return {
    products,
    isLoading,
    error,
    refetch: fetchProducts,
    searchProducts,
    filterByCategory,
    getCategories,
  };
}

// Get a single product by ID
export async function getDemoProductById(
  productId: string,
): Promise<DemoProductItem | null> {
  try {
    const db = await getDemoDatabase();
    const product = await getDemoRow(db, 'demo_products', productId);
    return toDemoProductItem(product);
  } catch (err) {
    console.error('[getDemoProductById] Error:', err);
    return null;
  }
}

// Update product stock (for demo cart operations)
export async function updateDemoProductStock(
  productId: string,
  quantityChange: number,
): Promise<boolean> {
  try {
    const db = await getDemoDatabase();
    await db.writeTransaction(async tx => {
      const product = await getDemoRow(tx, 'demo_products', productId);
      const currentQty = readNumber(product.bp_product_quantity);
      await updateDemoRow(tx, 'demo_products', productId, {
        bp_product_quantity: Math.max(0, currentQty - quantityChange),
        bp_product_updated_at: Date.now(),
      });
    });
    return true;
  } catch (err) {
    console.error('[updateDemoProductStock] Error:', err);
    return false;
  }
}
